import type { TmsEvent, TmsEventStream, TmsEventStreamPage, TmsEventStreamQuery } from "@storyboard/web-client";
import { listTmsEventStream, serverNow } from "@storyboard/web-client";
import { transport } from "@/config";
import { createLogger } from "@/logger";
import { bulkUpsertTmsEvent } from "@/models/repositories/TmsEvent";
import {
  coverageKey,
  coverageStoreMigratingFrom,
  registryCoverageStore,
  registryCursorStore,
} from "@/models/sync/cursorStore";
import { COLD_BOOT_LADDER_HOURS, type CoverageStore, createCoverageSync } from "@/models/sync/eventLogSync";
import type { SyncContext } from "@/models/sync/syncTargets";
import { parseTmsEvents } from "@/models/tmsEvent/parse";
import { ALL_TMS_EVENT_STREAMS, TMS_EVENT_STREAMS } from "@/models/tmsEvent/stream";
import { recordEvent } from "@/services/telemetry";

const log = createLogger("appsync");

/** How far back a workspace's events are held: two days of the whole workspace's events. */
export const TMS_EVENT_BOOTSTRAP_DEPTH = { hours: 48 } as const;

/**
 * Events per page the client asks for: the most the route serves.
 *
 * A large fleet creates this many events in a few hours, so a cold boot at a smaller page would cost hundreds of
 * requests; at the cap it is a handful, each one store write.
 */
export const TMS_EVENT_PAGE_LIMIT = 10_000;

/** Pages one gap fill may take before it stops; its progress is kept, and the next sync resumes from it. */
export const TMS_EVENT_MAX_DELTA_PAGES = 50;

/** The log every stream's coverage is keyed under. */
const TMS_EVENT_LOG = "tmsEvents";

/** Registry key of a stream's legacy cursor, read once to seed its coverage; never written any more. */
export function tmsEventCursorKey(stream: TmsEventStream): string {
  return `tmsEvents:${stream}Cursor`;
}

/** Registry key of a stream's coverage. */
export function tmsEventCoverageKey(stream: TmsEventStream): string {
  return coverageKey(TMS_EVENT_LOG, stream);
}

/** The newest `createdAt` on a page; an event that states none is skipped. */
export function newestCreatedAtOf(page: TmsEventStreamPage): Date | undefined {
  let newest: number | undefined;
  for (const event of page.events) {
    if (event.createdAt == null) continue;
    const created = Date.parse(event.createdAt);
    if (newest === undefined || created > newest) newest = created;
  }
  return newest === undefined ? undefined : new Date(newest);
}

/** The collaborators the sync reads and writes through; production binds the transport, the registry and the store. */
export type TmsEventSyncDeps = {
  fetchPage: (query: TmsEventStreamQuery) => Promise<TmsEventStreamPage>;
  coverage: CoverageStore;
  apply: (events: readonly TmsEvent[]) => Promise<void>;
  /** A recoverable condition worth a reliability event: a row this build cannot read, a walk that hit its cap. */
  report: (name: string, attributes: Record<string, unknown>) => void;
  /** The server's clock as the client estimates it from every response's `Date` header. */
  now: () => Date;
};

export type TmsEventSync = {
  sync: (ctx: SyncContext, stream: TmsEventStream) => Promise<void>;
  /** Resolves once no background fill is running for the stream; see `CoverageSync.backfilled`. */
  backfilled: (ctx: SyncContext, stream: TmsEventStream) => Promise<void>;
};

/**
 * Binds the coverage engine to the TMS event streams.
 *
 * Every stream is one log on the `createdAt` axis, read in delta mode: `since` and the server's cursor, never
 * history mode. The engine decides what to fetch; this binding decides how a page is fetched, parsed and landed.
 */
export function createTmsEventSync(deps: TmsEventSyncDeps): TmsEventSync {
  /** The page's readable events, reporting each one this build cannot read. */
  function parsePage(page: TmsEventStreamPage): TmsEvent[] {
    const { events, rejected } = parseTmsEvents(page.events);
    for (const row of rejected) {
      deps.report("tmsEvents.event_rejected", {
        tmsEvent: { stream: page.stream, kind: row.kind, version: row.version },
      });
    }
    return events;
  }

  const engine = createCoverageSync<TmsEventStream, TmsEventStreamPage>({
    depth: TMS_EVENT_BOOTSTRAP_DEPTH,
    ladderHours: COLD_BOOT_LADDER_HOURS,
    maxPages: TMS_EVENT_MAX_DELTA_PAGES,
    store: deps.coverage,
    keyOf: tmsEventCoverageKey,
    fetchPage: (ctx, stream, since, cursor) =>
      deps.fetchPage({
        workspaceId: ctx.workspaceId,
        stream,
        limit: TMS_EVENT_PAGE_LIMIT,
        since: since.toISOString(),
        cursor,
      }),
    applyPage: (page) => deps.apply(parsePage(page)),
    newestCreatedAt: newestCreatedAtOf,
    now: deps.now,
    report: (event, stream, attributes) => deps.report(`tmsEvents.${event}`, { tmsEvent: { stream, ...attributes } }),
  });

  return { sync: engine.sync, backfilled: engine.backfilled };
}

const production = createTmsEventSync({
  fetchPage: (query) => listTmsEventStream(transport, query),
  coverage: coverageStoreMigratingFrom(
    registryCoverageStore,
    registryCursorStore,
    new Map(ALL_TMS_EVENT_STREAMS.map((stream) => [tmsEventCoverageKey(stream), tmsEventCursorKey(stream)])),
    TMS_EVENT_BOOTSTRAP_DEPTH,
    serverNow,
  ),
  apply: bulkUpsertTmsEvent,
  report: (name, attributes) => recordEvent(name, attributes, "reliability"),
  now: serverNow,
});

/**
 * Brings the stream's whole window up to date, newest rows first.
 *
 * On a cold boot the recent window lands first and the rest of the window behind it, and the call resolves once all
 * of it has landed; on a warm boot, once the rows created since the last sync have landed: one request within an hour
 * of it, and the newest hour first, then the rest, when longer. The window is fixed at `TMS_EVENT_BOOTSTRAP_DEPTH`.
 */
export async function bootstrapTmsEventStream(ctx: SyncContext, stream: TmsEventStream): Promise<void> {
  await production.sync(ctx, stream);
  await production.backfilled(ctx, stream);
}

/**
 * Fetches the rows created since the stream's newest held span and lands them: a poll, or a ping brought forward.
 * A stream with no coverage cold boots instead.
 */
export function walkTmsEventDelta(ctx: SyncContext, stream: TmsEventStream): Promise<void> {
  return production.sync(ctx, stream);
}

/**
 * Brings every stream up to date at workspace boot; a stream that fails is logged and left to its next poll.
 *
 * Resolves once each stream's whole window has landed, so a boot that waits on it holds the window.
 */
export async function bootstrapTmsEventStreams(ctx: SyncContext): Promise<void> {
  await Promise.all(
    TMS_EVENT_STREAMS.map(async (stream) => {
      try {
        await bootstrapTmsEventStream(ctx, stream);
      } catch (error) {
        log.warn("[sync] TMS event bootstrap failed; the next poll retries", { stream, error });
      }
    }),
  );
}
